import { useEffect, useState } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import BrandPanel from "../components/BrandPanel";
import LangToggle from "../components/LangToggle";
import { ShieldCheck, Smartphone, Copy, Loader2, Download, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Two-step TOTP enrollment screen:
 *  Step 1 (qr):    Display QR code, secret string, hint to scan with Authenticator
 *  Step 2 (verify): User enters first 6-digit code → backend activates TOTP +
 *                   returns 8 one-time backup codes. We show them to the user
 *                   ONCE and they must save them before continuing to /.
 *
 * Navigation: location.state = { setup_token: string, company_name: string }
 */
export default function TotpSetup() {
    const navigate = useNavigate();
    const location = useLocation();
    const { refresh } = useAuth();
    const { lang, t, toggle } = useLang();

    const initialToken = location.state?.setup_token;
    const companyName = location.state?.company_name || "";
    const email = location.state?.email || "";

    const [setupToken, setSetupToken] = useState(initialToken || "");
    const [qrUrl, setQrUrl] = useState("");
    const [secret, setSecret] = useState("");
    const [step, setStep] = useState("qr"); // "qr" | "verify" | "done"
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [backupCodes, setBackupCodes] = useState([]);
    const [savedAcknowledged, setSavedAcknowledged] = useState(false);

    // If we got a token via state, immediately request the QR.
    useEffect(() => {
        if (!initialToken) return;
        let cancel = false;
        (async () => {
            setBusy(true);
            try {
                const { data } = await api.post(`/auth/totp/setup?setup_token=${encodeURIComponent(initialToken)}`);
                if (cancel) return;
                setQrUrl(data.qr_data_url);
                setSecret(data.secret);
                setSetupToken(data.setup_token);
            } catch (e) {
                const msg = formatApiError(e.response?.data?.detail) || e.message;
                setError(msg);
                toast.error(msg);
            } finally {
                if (!cancel) setBusy(false);
            }
        })();
        return () => { cancel = true; };
    }, [initialToken]);

    if (!initialToken) return <Navigate to="/login" replace />;

    const verify = async (e) => {
        e?.preventDefault?.();
        setError("");
        const clean = (code || "").replace(/\D/g, "");
        if (!/^\d{6}$/.test(clean)) {
            setError(t.totpInvalidFormat);
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/auth/totp/setup/verify", {
                setup_token: setupToken, code: clean,
            });
            setBackupCodes(data.backup_codes || []);
            setStep("done");
            toast.success(t.totpEnabledToast);
        } catch (e2) {
            const msg = formatApiError(e2.response?.data?.detail) || e2.message;
            setError(msg);
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    const copySecret = async () => {
        try { await navigator.clipboard.writeText(secret); toast.success(t.copiedToast); } catch { /* clipboard unavailable */ }
    };

    const copyAllCodes = async () => {
        try {
            await navigator.clipboard.writeText(backupCodes.join("\n"));
            toast.success(t.copiedToast);
        } catch { /* clipboard unavailable */ }
    };

    const downloadCodes = () => {
        const blob = new Blob(
            [`${t.brand} — ${t.totpBackupCodesTitle}\n${companyName}\n\n${backupCodes.join("\n")}\n\n${t.totpBackupCodesFootnote}`],
            { type: "text/plain;charset=utf-8" },
        );
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `meyar-backup-codes-${companyName || "account"}.txt`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const finishSetup = async () => {
        await refresh();
        navigate("/", { replace: true });
    };

    return (
        <div className="relative min-h-screen w-full app-bg" dir={t.dir}>
            <LangToggle lang={lang} onToggle={toggle} t={t} />
            <div className="mx-auto grid min-h-screen max-w-7xl grid-cols-1 lg:grid-cols-2">
                <div className="relative hidden p-6 lg:block">
                    <BrandPanel lang={lang} strings={t} />
                </div>
                <div className="flex items-center justify-center px-4 py-8 sm:px-6 sm:py-12 lg:px-12">
                    <div className="w-full max-w-md fade-up">
                        <div className="mb-6 flex items-center gap-3">
                            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-700 text-white">
                                <ShieldCheck className="h-5 w-5" />
                            </div>
                            <div>
                                <h1 className="font-heading text-xl font-bold sm:text-2xl">{t.totpSetupTitle}</h1>
                                <p className="text-xs text-neutral-500 sm:text-sm">{companyName}</p>
                                {email ? (
                                    <p className="mt-0.5 font-numbers text-xs text-amber-700" dir="ltr" data-testid="totp-setup-account-email">
                                        {email}
                                    </p>
                                ) : null}
                            </div>
                        </div>

                        {step === "qr" || step === "verify" ? (
                            <>
                                <p className="text-sm text-neutral-600">{t.totpSetupSubtitle}</p>

                                <div className="mt-4 rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm">
                                    <div className="text-xs font-bold uppercase tracking-wider text-neutral-500">{t.totpSetupStep1}</div>
                                    <p className="mt-1 text-xs text-neutral-600">{t.totpSetupStep1Hint}</p>
                                    {email ? (
                                        <div className="mt-2 rounded-md bg-emerald-50 px-3 py-1.5 text-xs text-emerald-900 ring-1 ring-emerald-200">
                                            <span className="font-semibold">{t.totpAccountInAuth}:</span>{" "}
                                            <span className="font-numbers" dir="ltr">{email}</span>
                                        </div>
                                    ) : null}
                                    <div className="mt-3 flex justify-center" data-testid="totp-qr-wrap">
                                        {busy && !qrUrl ? (
                                            <Loader2 className="my-12 h-8 w-8 animate-spin text-neutral-400" />
                                        ) : qrUrl ? (
                                            <img src={qrUrl} alt="TOTP QR" className="h-44 w-44 rounded-lg border border-neutral-200" data-testid="totp-qr-img" />
                                        ) : null}
                                    </div>
                                    <div className="mt-3">
                                        <Label className="text-xs text-neutral-500">{t.totpSecretLabel}</Label>
                                        <div className="mt-1 flex items-center gap-2 rounded-md bg-neutral-50 px-2 py-1.5">
                                            <code className="flex-1 break-all font-numbers text-xs text-neutral-700" data-testid="totp-secret-value">{secret || "—"}</code>
                                            <button type="button" onClick={copySecret} className="rounded p-1 hover:bg-neutral-200" data-testid="totp-copy-secret-btn">
                                                <Copy className="h-3.5 w-3.5 text-neutral-600" />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <form onSubmit={verify} className="mt-4 rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm">
                                    <div className="text-xs font-bold uppercase tracking-wider text-neutral-500">{t.totpSetupStep2}</div>
                                    <p className="mt-1 text-xs text-neutral-600">{t.totpSetupStep2Hint}</p>
                                    <div className="mt-3">
                                        <Label className="mb-1.5 block text-sm font-medium">{t.totpCodeLabel}</Label>
                                        <div className="relative">
                                            <Smartphone className={`pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400 ${lang === "ar" ? "right-3" : "left-3"}`} />
                                            <Input
                                                value={code}
                                                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                                className={`h-11 ${lang === "ar" ? "pe-10 ps-3" : "ps-10 pe-3"} font-numbers tracking-[0.4em] text-center text-lg`}
                                                placeholder="••••••"
                                                inputMode="numeric"
                                                autoFocus
                                                data-testid="totp-setup-code-input"
                                            />
                                        </div>
                                    </div>
                                    {error ? (
                                        <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="totp-setup-error">
                                            {error}
                                        </div>
                                    ) : null}
                                    <Button
                                        type="submit"
                                        disabled={busy || !qrUrl}
                                        className="mt-4 h-11 w-full bg-emerald-700 text-white hover:bg-emerald-800"
                                        data-testid="totp-setup-verify-btn"
                                    >
                                        {busy ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : null}
                                        {t.totpActivateBtn}
                                    </Button>
                                </form>
                            </>
                        ) : (
                            // step === "done"
                            <>
                                <div className="mb-4 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                                    <CheckCircle2 className="h-4 w-4" />
                                    <span>{t.totpEnabledBanner}</span>
                                </div>
                                <h2 className="font-heading text-xl font-bold">{t.totpBackupCodesTitle}</h2>
                                <p className="mt-1 text-sm text-neutral-600">{t.totpBackupCodesSubtitle}</p>
                                <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50/40 p-3" data-testid="backup-codes-grid">
                                    <ul className="grid grid-cols-2 gap-2 font-numbers text-sm">
                                        {backupCodes.map((c, i) => (
                                            <li key={c} className="rounded-md bg-white px-2 py-2 text-center font-bold text-neutral-800 ring-1 ring-amber-200" data-testid={`backup-code-${i}`}>
                                                {c}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="mt-3 flex gap-2">
                                    <Button type="button" variant="outline" onClick={copyAllCodes} className="flex-1" data-testid="copy-backup-codes-btn">
                                        <Copy className="me-1 h-4 w-4" />
                                        {t.totpCopyAll}
                                    </Button>
                                    <Button type="button" variant="outline" onClick={downloadCodes} className="flex-1" data-testid="download-backup-codes-btn">
                                        <Download className="me-1 h-4 w-4" />
                                        {t.totpDownloadTxt}
                                    </Button>
                                </div>
                                <label className="mt-4 flex items-start gap-2 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={savedAcknowledged}
                                        onChange={(e) => setSavedAcknowledged(e.target.checked)}
                                        className="mt-0.5"
                                        data-testid="backup-codes-saved-checkbox"
                                    />
                                    <span>{t.totpAckSaved}</span>
                                </label>
                                <Button
                                    type="button"
                                    disabled={!savedAcknowledged}
                                    onClick={finishSetup}
                                    className="mt-4 h-11 w-full bg-neutral-900 text-white hover:bg-neutral-800"
                                    data-testid="finish-setup-btn"
                                >
                                    {t.totpFinishSetup}
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
